import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Package, FileText, Plus, History } from 'lucide-react';
import axios from 'axios';
import { POGrnItemRow } from './POGrnItemRow';
import { GRNHistoryCard } from './GRNHistoryCard';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function POGrnTab({ purchaseOrder, onGrnSuccess, hasPermission }) {
  const [grnHistory, setGrnHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [selectedGrnId, setSelectedGrnId] = useState(null);
  
  const fetchGrnHistory = useCallback(async () => {
    if (!purchaseOrder?.id) return;
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/grn/po/${purchaseOrder.id}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGrnHistory(response.data);
    } catch {
      setGrnHistory({ grns: [], summary: { total_ordered: 0, total_received: 0, total_pending: 0 } });
    } finally {
      setLoadingHistory(false);
    }
  }, [purchaseOrder?.id]);
  
  useEffect(() => {
    fetchGrnHistory();
  }, [fetchGrnHistory]);
  
  if (!purchaseOrder) {
    return (
      <div className="py-12 text-center text-gray-500">
        <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>Save the Purchase Order first to enable receiving</p>
      </div>
    );
  }
  
  const isReceivable = ['approved', 'partially_received'].includes(purchaseOrder.status);
  const isFullyReceived = purchaseOrder.status === 'received';
  const hasPendingQty = grnHistory?.summary?.total_pending > 0;
  const completionPct = grnHistory?.summary?.completion_percentage || 0;
  
  let statusBadgeClass = 'bg-gray-100 text-gray-800';
  let statusText = purchaseOrder.status.replace('_', ' ').toUpperCase();
  
  if (isFullyReceived) {
    statusBadgeClass = 'bg-green-100 text-green-800';
    statusText = 'FULLY RECEIVED';
  } else if (purchaseOrder.status === 'partially_received') {
    statusBadgeClass = 'bg-orange-100 text-orange-800';
    statusText = 'PARTIALLY RECEIVED';
  } else if (isReceivable) {
    statusBadgeClass = 'bg-blue-100 text-blue-800';
    statusText = 'AWAITING GRN';
  }

  function handleGrnToggle(grnId) {
    if (selectedGrnId === grnId) {
      setSelectedGrnId(null);
    } else {
      setSelectedGrnId(grnId);
    }
  }

  function goToGrnPage() {
    window.location.href = '/grn';
  }

  return (
    <div className="space-y-6" data-testid="po-grn-tab">
      <SummaryCard
        completionPct={completionPct}
        grnHistory={grnHistory}
        loadingHistory={loadingHistory}
        statusBadgeClass={statusBadgeClass}
        statusText={statusText}
      />
      
      <ItemSummarySection
        purchaseOrder={purchaseOrder}
        grnHistory={grnHistory}
        loadingHistory={loadingHistory}
      />
      
      <GRNHistorySection
        grnHistory={grnHistory}
        loadingHistory={loadingHistory}
        isReceivable={isReceivable}
        selectedGrnId={selectedGrnId}
        onToggle={handleGrnToggle}
      />
      
      {hasPendingQty && isReceivable && hasPermission && (
        <div className="flex justify-center">
          <Button className="gap-2 bg-blue-600 hover:bg-blue-700" onClick={goToGrnPage}>
            <Plus className="w-4 h-4" />
            Create New GRN
          </Button>
        </div>
      )}
    </div>
  );
}

export default POGrnTab;

function SummaryCard({ completionPct, grnHistory, loadingHistory, statusBadgeClass, statusText }) {
  const totalOrdered = grnHistory?.summary?.total_ordered || 0;
  const totalReceived = grnHistory?.summary?.total_received || 0;
  const totalPending = grnHistory?.summary?.total_pending || 0;
  const grnCount = grnHistory?.grn_count || 0;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            GRN Summary
          </h3>
          <Badge className={statusBadgeClass}>{statusText}</Badge>
        </div>
        
        {loadingHistory ? (
          <div className="text-center py-4 text-gray-500">Loading...</div>
        ) : (
          <div>
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Receiving Progress</span>
                <span className="font-medium text-gray-800">{completionPct}%</span>
              </div>
              <Progress value={completionPct} className="h-3" />
            </div>
            
            <div className="grid grid-cols-4 gap-4 text-center">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{totalOrdered}</p>
                <p className="text-xs text-blue-700">Ordered Qty</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{totalReceived}</p>
                <p className="text-xs text-green-700">Received Qty</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="text-2xl font-bold text-orange-600">{totalPending}</p>
                <p className="text-xs text-orange-700">Pending Qty</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">{grnCount}</p>
                <p className="text-xs text-purple-700">GRNs Created</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ItemSummarySection({ purchaseOrder, grnHistory, loadingHistory }) {
  if (loadingHistory || !grnHistory?.item_summary) {
    return null;
  }

  const items = purchaseOrder.items || [];
  
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-600" />
          Item-wise Summary
        </h3>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left font-semibold">Product</th>
                <th className="px-4 py-2 text-center font-semibold w-24">Ordered</th>
                <th className="px-4 py-2 text-center font-semibold w-24">Received</th>
                <th className="px-4 py-2 text-center font-semibold w-24">Pending</th>
                <th className="px-4 py-2 text-center font-semibold w-24">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((item, idx) => (
                <POGrnItemRow key={item.product_id || `itm-${idx}`} item={item} idx={idx} grnHistory={grnHistory} />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function GRNHistorySection({ grnHistory, loadingHistory, isReceivable, selectedGrnId, onToggle }) {
  const grns = grnHistory?.grns || [];
  const grnCount = grnHistory?.grn_count || 0;

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <History className="w-5 h-5 text-green-600" />
          GRN History ({grnCount} GRNs)
        </h3>
        
        {loadingHistory ? (
          <div className="text-center py-4 text-gray-500">Loading...</div>
        ) : grns.length > 0 ? (
          <div className="space-y-3">
            {grns.map((grn, idx) => (
              <GRNHistoryCard
                key={grn.id}
                grn={grn}
                idx={idx}
                isExpanded={selectedGrnId === grn.id}
                onToggle={() => onToggle(grn.id)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No GRNs created yet</p>
            {isReceivable && (
              <p className="text-xs mt-1">Use the GRN module to create goods receipt</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
